import comboComponent from "@/components/child/form/combo.vue";
import dateTimePickerComponent from "@/components/child/form/datetimepicker.vue";
import { GridAction } from "@/components/child/grid/gridAction";
import { GridColors, GridColumn, GridColumnType } from "@/components/child/grid/gridColumn";
import multiSelectComponent from "@/components/child/multiSelect/multiSelect.vue";
import { Component } from "@/decorators";
import PaginationModel from "@/models/paginationModel";
import VendaModel, { VendaListParameters } from "@/models/vendaModel";
import NotaFiscalEletronicaEmissaoService from "@/services/notaFiscal/notaFiscalEletronicaEmissaoService";
import EtiquetaTransportadoraPDFService from "@/services/pdf/EtiquetaTransportadoraPDFService";
import PDFService from "@/services/pdf/PDFService";
import localEntregaService from "@/services/transporte/localEntregaService";
import VendaService from "@/services/vendaService";
import { convertToDateWithTimeInputString } from "@/utils/common/date";

import ListComponentBase from "../listComponentBase";

const vendaService = new VendaService();
const pdfService = new PDFService("RomaneioEntregaPDF");
const etiquetaTransportadoraPDFService = new EtiquetaTransportadoraPDFService();
const notaFiscalEletronicaEmissaoService = new NotaFiscalEletronicaEmissaoService();

@Component({
    components: {
        dateTimePickerComponent,
        comboComponent,
        multiSelectComponent,
    },
})
export default class RomaneioEntregaListComponent extends ListComponentBase<VendaModel> {
    dataFilter1: Date = null;
    dataFilter2: Date = null;
    localEntregaIds: Array<Object> = [];
    filtro: Array<Object> = [];

    isHidden = false;

    locaisEntregaOptions: Array<Object> = [];
    gridSelectedValues: Array<number> = [];

    extraActions: GridAction[][] = [];
    extraActionNotaTransporte: GridAction[] = [];
    extraActionImprimirTransportadora: GridAction[] = [];

    get gridColumns(): Array<GridColumn> {
        return [
            new GridColumn(
                "codigo",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.codigo"),
                GridColumnType.String,
            ),
            new GridColumn(
                "clienteNome",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.nomeCliente"),
                GridColumnType.String,
            ),
            new GridColumn(
                "enderecoEntrega",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.enderecoEntrega"),
                GridColumnType.String,
            ),
            new GridColumn(
                "localEntrega",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.localEntrega"),
                GridColumnType.String,
            ),
            new GridColumn(
                "transportadoraDescricao",
                this.$t("__.Crud.localentrega.edit_vue_html.transportadora"),
                GridColumnType.String,
            ),
            new GridColumn(
                "dataEntrega",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.dataEntrega"),
                GridColumnType.DateTime,
            ),
            new GridColumn(
                "valorFinal",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.valorFinal"),
                GridColumnType.Money,
            ),
            new GridColumn(
                "volume",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.volume"),
                GridColumnType.Integer,
            ),
            new GridColumn(
                "setor",
                this.$t("__.Components.parent.crud.expedicao.list_vue_html.setor"),
                GridColumnType.String,
            ),
        ];
    }

    public async afterLoad() {
        this.gridData["dataDe"] = this.filtro[0] ? this.filtro[0].toString() : " ";
        this.gridData["dataAte"] = this.filtro[1] ? this.filtro[1].toString() : " ";

        this.locaisEntregaOptions = await localEntregaService.comboCached(this.usuarioLogado.franquiaId);

        this.extraActions.splice(0);
        this.extraActionNotaTransporte.splice(0);
        this.extraActionImprimirTransportadora.splice(0);

        for (let i = 0; i < this.gridData.length; i++) {
            this.extraActionNotaTransporte.push(
                new GridAction(
                    "abrir-nota-transporte",
                    this.$t("__.Crud.expedicao.list_vue_html.notaTransporte") as string,
                    "fa fa-file-pdf",
                    GridColors.LIGHTRED,
                    !!this.gridData[i].notaTransportePdfSimplificadoLink,
                ),
            );

            this.extraActionImprimirTransportadora.push(
                new GridAction(
                    "imprimir-transportadora",
                    this.$t("__.Crud.expedicao.list_vue_html.imprimirEtiquetaTransportadora") as string,
                    "fa fa-print",
                    GridColors.GREEN,
                ),
            );
        }

        this.extraActions.push(this.extraActionNotaTransporte);
        this.extraActions.push(this.extraActionImprimirTransportadora);
    }

    public onAdvancedFilter() {
        this.isHidden = !this.isHidden;
        this.load();
    }

    private onSelectedValuesChanged(values: Array<VendaModel>) {
        this.gridSelectedValues = values.map(p => p.id);
    }

    private async onExtraActions(name: string, venda: VendaModel) {
        if (name.trim() == "imprimir-transportadora") {
            await etiquetaTransportadoraPDFService.geraPDF(venda.id);
        } else if (name.trim() == "abrir-nota-transporte") {
            notaFiscalEletronicaEmissaoService.openPdf(venda.notaTransportePdfSimplificadoLink);
        }
    }

    public async onImprimirEtiquetasTransportadoraLote() {
        try {
            const base64Data = await etiquetaTransportadoraPDFService
                .getListEtiquetasByVendaIds(this.gridSelectedValues)
                .withLoading()
                .resolveWithText();

            if (!base64Data) {
                await this.$showError(this.$t("__.ts.erro"), this.$t("__.ts.nenhumaEtiqueta"));
                return;
            }

            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            const blobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = blobUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                iframe.contentWindow?.print();
                URL.revokeObjectURL(blobUrl);
            };
        } catch (error) {
            await this.$showError(this.$t("__.ts.erro"), this.$t("__.ts.erroGerarEtiq"));
        }
    }

    private async imprimirRomaneio() {
        await pdfService.geraPDFWithFilters(this.gridSelectedValues);
    }

    public async loadMethod() {
        const customParameters: VendaListParameters = {
            dataInicial: convertToDateWithTimeInputString(this.dataFilter1 ?? this.filtro[0]),
            dataFinal: convertToDateWithTimeInputString(this.dataFilter2 ?? this.filtro[1]),
            locaisEntregas: this.localEntregaIds.map(p => p["value"]),
        };

        return await vendaService
            .getListPaginationRomaneioEntrega(
                this.gridFilterKey,
                this.gridSortKey,
                this.gridSortOrder,
                this.pags ? 1 : this.pageIndex,
                this.pags ? 999999 : this.pageSize,
                customParameters,
            )
            .withLoading()
            .resolveWithJSON<PaginationModel<VendaModel>>();
    }

    private beforeMount() {
        this.setProps(new VendaService(), "venda", "dataEntrega", false, "desc");
    }
}
